import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Descriptions,
  Form,
  Input,
  Modal,
  Spin,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import { MAX_LONGITUD_TEXTO, esErrorDeContrasena } from 'api/procesosMasivos/descuentosMasivos';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';

const formatNumero = (valor) => (valor ?? 0).toLocaleString('es-MX');

const ModalCancelarDescuentosMasivos = ({
  visible,
  descuentos,
  totalDeCuentas,
  onRegresar,
  onCancelar,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // { ok, data, error } que regresa cancelarDescuentosMasivos
  const [resultado, setResultado] = useState(null);
  const [errorDeContrasena, setErrorDeContrasena] = useState(null);

  // Se limpia al abrir y al cerrar, para no conservar la contraseña
  useEffect(() => {
    form.resetFields();
    setResultado(null);
    setErrorDeContrasena(null);
  }, [visible]);

  const onAceptar = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setLoading(true);
    setErrorDeContrasena(null);
    const _resultado = await onCancelar({
      motivo: values.motivo_de_cancelacion.trim(),
      password: values.password,
    });
    setLoading(false);
    form.resetFields(['password']);
    // Con contraseña incorrecta no se procesó nada: se permite reintentar
    if (!_resultado.ok && esErrorDeContrasena(_resultado.error)) {
      setErrorDeContrasena(_resultado.error);
      return;
    }
    setResultado(_resultado);
  };

  const resumen = resultado?.data;
  const controls = resultado
    ? [{ text: 'Cerrar', icon: <CheckOutlined />, onClick: onRegresar }]
    : [
      { text: 'Regresar', icon: <ArrowLeftOutlined />, onClick: onRegresar },
      { text: 'Aceptar', icon: <CheckOutlined />, onClick: onAceptar },
    ];

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={!loading}
      width={600}
      centered
      title={(
        <FormSubmitCustomControls
          label={resultado ? 'Resultado de la cancelación' : 'Confirmación'}
          loading={loading}
          controls={controls}
        />
      )}
    >
      <Spin tip="Cancelando descuentos..." spinning={loading}>
        {!resultado && (
          <Form form={form} layout="vertical" onFinish={onAceptar}>
            <Typography.Paragraph>
              {`¿Estás seguro de querer cancelar los siguientes descuentos aplicados a las ${formatNumero(totalDeCuentas)} cuenta(s) seleccionadas?`}
            </Typography.Paragraph>
            <ul>
              {descuentos.map((descuento) => (
                <li key={descuento.id}>
                  <Typography.Text strong>
                    {`${descuento.clave} - ${descuento.descripcion}`}
                  </Typography.Text>
                </li>
              ))}
            </ul>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Esta acción puede tardar varios minutos. No se cancelan descuentos con abonos ni de cargos en convenio."
            />
            <Form.Item
              name="motivo_de_cancelacion"
              label="Motivo de cancelación"
              rules={[{
                required: true,
                whitespace: true,
                message: 'Capture el motivo de cancelación',
              }]}
            >
              <Input.TextArea
                autoSize={{ minRows: 3, maxRows: 5 }}
                maxLength={MAX_LONGITUD_TEXTO}
                showCount
                allowClear
              />
            </Form.Item>
            {!!errorDeContrasena && (
              <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorDeContrasena} />
            )}
            <Form.Item
              name="password"
              label="Contraseña (confirme con la contraseña de su usuario)"
              rules={[{ required: true, message: 'Capture su contraseña' }]}
            >
              <Input.Password autoComplete="current-password" />
            </Form.Item>
          </Form>
        )}
        {resultado && (
          <>
            <Alert
              type={resultado.ok ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 12 }}
              message={resultado.ok ? resumen?.detail : resultado.error}
            />
            {!!resumen?.total_de_cuentas && (
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Cuentas solicitadas">
                  {formatNumero(resumen.total_de_cuentas)}
                </Descriptions.Item>
                <Descriptions.Item label="Cuentas con descuentos cancelados">
                  {formatNumero(resumen.cuentas_con_descuentos_cancelados)}
                </Descriptions.Item>
                <Descriptions.Item label="Cuentas sin descuentos que cancelar">
                  {formatNumero(resumen.cuentas_sin_descuentos_cancelados)}
                </Descriptions.Item>
                <Descriptions.Item label="Descuentos cancelados">
                  {formatNumero(resumen.descuentos_cancelados)}
                  {(resumen.descuentos_cancelados_por_tipo?.length > 1) && (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {resumen.descuentos_cancelados_por_tipo.map((tipo) => (
                        <li key={tipo.tipo_de_descuento}>
                          {`${tipo.descripcion}: ${formatNumero(tipo.descuentos_cancelados)}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="No cancelados por convenio">
                  {formatNumero(resumen.descuentos_no_cancelados_por_convenio)}
                </Descriptions.Item>
                <Descriptions.Item label="No cancelados por abonos">
                  {formatNumero(resumen.descuentos_no_cancelados_por_abonos)}
                </Descriptions.Item>
                <Descriptions.Item label="Cuentas no encontradas">
                  {formatNumero(resumen.cuentas_no_encontradas)}
                  {!!resumen.claves_no_encontradas?.length && (
                    <Typography.Paragraph
                      type="secondary"
                      style={{ wordBreak: 'break-all', margin: 0 }}
                      ellipsis={{ rows: 3, expandable: true, symbol: 'Ver todas' }}
                    >
                      {resumen.claves_no_encontradas.join(', ')}
                    </Typography.Paragraph>
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
};

ModalCancelarDescuentosMasivos.propTypes = {
  visible: PropTypes.bool.isRequired,
  descuentos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    clave: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descripcion: PropTypes.string,
  })).isRequired,
  totalDeCuentas: PropTypes.number.isRequired,
  onRegresar: PropTypes.func.isRequired,
  // Recibe { motivo, password } y regresa { ok, data, error }
  onCancelar: PropTypes.func.isRequired,
};

export default ModalCancelarDescuentosMasivos;
